import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  PhonicsLesson,
  PhonicsLessonGroup,
  PhonicsClassStatus,
} from '@/api/types';
import { LETTER_SETS } from '@/types';
import { ManageYearWeekLessonAccordion } from './ManageYearWeekLessonAccordion';
import { ManageDecodableBookPod } from './ManageDecodableBookPod';
import { ManagePanel } from './ManagePanel';
import { getLessonsByLetterSetIndex } from '@/utils/lessons';

interface LetterSetProps {
  letterSet: Array<string>;
  letterSetIndex: number;
  number: number;
  lessonGroups: Array<PhonicsLessonGroup>;
}

const LetterSet = ({
  letterSet,
  letterSetIndex,
  number,
  lessonGroups,
}: LetterSetProps) => {
  const lessons: Array<PhonicsLesson> = useMemo(
    () => getLessonsByLetterSetIndex(letterSetIndex, lessonGroups),
    [lessonGroups, letterSetIndex],
  );

  return (
    <ManagePanel
      title={`Letter set ${number}`}
      titleWithoutUnderline
      titleInnerClassName="text-xl text-pt-primary dark:text-pt-lightening-yellow"
      yOrientation
      TitleElement="h2"
      spacing="sm"
    >
      <h3 className="text-lg">{letterSet.join('/')}</h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {lessons
          .filter((lesson) => !!lesson)
          .map(
            (lesson) =>
              lesson.decodableBookIds &&
              lesson.decodableBookIds.length > 0 &&
              lesson.decodableBookIds?.map((bookId) => (
                <ManageDecodableBookPod key={bookId} bookId={bookId} />
              )),
          )}
      </div>
    </ManagePanel>
  );
};

interface ManageDecodableBookListProps {
  lessonGroups: Array<PhonicsLessonGroup>;
  selectedLetterSetIndex?: string;
}

export const ManageDecodableBooksList = ({
  lessonGroups,
  selectedLetterSetIndex,
}: ManageDecodableBookListProps) => {
  return (
    <div className="flex flex-col gap-6">
      {LETTER_SETS.map(
        (letterSet, index) =>
          (selectedLetterSetIndex === undefined ||
            parseInt(selectedLetterSetIndex, 10) === index) && (
            <LetterSet
              key={index}
              letterSet={letterSet}
              letterSetIndex={index}
              number={index + 1}
              lessonGroups={lessonGroups}
            />
          ),
      )}
    </div>
  );
};

interface ManageDecodableBookList2Props {
  lessonGroups: Array<PhonicsLessonGroup>;
  phonicsClass: PhonicsClassStatus;
}

export const ManageDecodableBooksList2 = ({
  lessonGroups,
  phonicsClass,
}: ManageDecodableBookList2Props) => {
  const [openYearGroup, setOpenYearGroup] = useState<
    PhonicsLessonGroup | undefined
  >();

  const [openWeekGroup, setOpenWeekGroup] = useState<
    PhonicsLessonGroup | undefined
  >();

  const getSoundsAndPatternsString = useCallback((lesson: PhonicsLesson) => {
    const symbols = [
      ...(lesson.sounds?.map((sound) => sound.name) || []),
      ...(lesson.patterns?.map((sound) => sound.name) || []),
    ];

    return Array.from(new Set(symbols)).join(', ');
  }, []);

  useEffect(() => {
    if (!phonicsClass) {
      return;
    }

    setOpenYearGroup(
      lessonGroups.find((yearGroup) =>
        yearGroup.children?.find((weekGroup) => {
          const isOpen = weekGroup.lessons?.find(
            (lesson) => lesson.id === phonicsClass.currentLessonId,
          );

          if (isOpen) {
            setOpenWeekGroup(weekGroup);
          }

          return isOpen;
        }),
      ),
    );
  }, [phonicsClass, lessonGroups]);

  return (
    <ManageYearWeekLessonAccordion
      lessonGroups={lessonGroups}
      openYearGroup={openYearGroup}
      setOpenYearGroup={setOpenYearGroup}
      openWeekGroup={openWeekGroup}
      setOpenWeekGroup={setOpenWeekGroup}
      onRenderLesson={(lesson) => (
        <div className="bg-white p-3 rounded-xl flex flex-col gap-3">
          <h4 className="font-bold text-lg text-pt-primary dark:text-pt-lightening-yellow text-left">
            Lesson {lesson.number} ({getSoundsAndPatternsString(lesson)})
          </h4>

          {lesson.decodableBookIds && lesson.decodableBookIds.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              {lesson.decodableBookIds?.map((bookId) => (
                <ManageDecodableBookPod key={bookId} bookId={bookId} />
              ))}
            </div>
          )}
        </div>
      )}
      onGetWeekSubHeading={(weekGroup) => {
        const subHeadings = weekGroup.lessons
          ?.filter((lesson) => !lesson.isReview)
          .flatMap((lesson) => [
            ...(lesson.sounds?.map((sound) => sound.name) || []),
            ...(lesson.patterns?.map((pattern) => pattern.name) || []),
          ]);

        return Array.from(new Set(subHeadings)).join(', ');
      }}
      podMode={false}
    />
  );
};
